import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  getDocuments,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../api/adminApi";
import { createNotificationEventSource } from "../../api/realtimeApi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../common/ToastProvider";
import { Icon } from "@iconify/react";

function formatRelativeDate(value) {
  if (!value) return "Unknown time";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Unknown time";

  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hr ago`;
  if (diffDay < 7) return `${diffDay} day ago`;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(d);
}

function normalizeNotificationType(type) {
  const value = String(type || "info").toLowerCase();
  if (["success", "warning", "error", "info"].includes(value)) {
    return value;
  }
  return "info";
}

export default function AdminLayout({
  title = "WOW-Sign Admin",
  subtitle = "Manage documents, signers, analytics, and workflows.",
  children,
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { session, logout } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [search, setSearch] = useState("");
  const [allDocs, setAllDocs] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [markingReadId, setMarkingReadId] = useState(null);
  const [notificationLiveConnected, setNotificationLiveConnected] =
    useState(false);

  const navClass = ({ isActive }) =>
    `sidebar-item gap-2 items-center ${isActive ? "active" : ""}`;

  useEffect(() => {
    const closeOnResize = () => {
      if (window.innerWidth > 1100) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", closeOnResize);
    return () => window.removeEventListener("resize", closeOnResize);
  }, []);

  useEffect(() => {
    const closeMenus = () => {
      setShowProfileMenu(false);
      setSearchOpen(false);
      setShowNotifications(false);
    };

    window.addEventListener("click", closeMenus);
    return () => window.removeEventListener("click", closeMenus);
  }, []);

  useEffect(() => {
    loadDocuments();
    loadNotifications(false);
  }, []);

  useEffect(() => {
    const eventSource = createNotificationEventSource();

    eventSource.addEventListener("connected", () => {
      setNotificationLiveConnected(true);
    });

    eventSource.addEventListener("notification-update", async () => {
      await loadNotifications(true);
    });

    eventSource.onerror = () => {
      setNotificationLiveConnected(false);
    };

    return () => {
      eventSource.close();
      setNotificationLiveConnected(false);
    };
  }, []);

  const loadDocuments = async () => {
    try {
      const res = await getDocuments();
      setAllDocs(res.data || []);
    } catch (e) {
      console.error("Failed to load global search documents", e);
    }
  };

  const loadNotifications = async (silent = false) => {
    try {
      if (!silent) {
        setNotificationsLoading(true);
      }

      const res = await getNotifications();
      const mapped = (res.data || []).map((item) => ({
        ...item,
        type: normalizeNotificationType(item.type),
      }));

      setNotifications(mapped);

      // if (silent && nextUnread > previousUnread) {
      //   toast.info("New notification received.");
      // }
    } catch (e) {
      if (!silent) {
        console.error("Failed to load notifications", e);
      }
    } finally {
      if (!silent) {
        setNotificationsLoading(false);
      }
    }
  };

  const filteredResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];

    return (allDocs || [])
      .filter((doc) =>
        String(doc.name || "")
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 6);
  }, [search, allDocs]);

  const unreadCount = useMemo(() => {
    return notifications.filter((item) => !item.read).length;
  }, [notifications]);

  const handleNavClick = () => {
    if (window.innerWidth <= 1100) {
      setSidebarOpen(false);
    }
  };

  const handleOpenResult = (doc) => {
    if (!doc?.id) return;
    setSearch("");
    setSearchOpen(false);
    navigate(`/documents/${doc.id}`);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (filteredResults.length > 0) {
        handleOpenResult(filteredResults[0]);
      } else if (search.trim()) {
        toast.info("No matching document found.");
      }
    }
  };

  const resolveNotificationTarget = (item) => {
    if (!item) return null;

    const directPath =
      item.path || item.route || item.url || item.link || item.targetPath;
    if (directPath && String(directPath).startsWith("/")) {
      return String(directPath);
    }

    if (item.documentId) {
      return `/documents/${item.documentId}`;
    }

    const action = String(item.action || item.activity || item.type || "")
      .toLowerCase()
      .trim();

    if (action.includes("analytics") || action.includes("sla")) {
      return "/analytics";
    }

    if (action.includes("dashboard") || action.includes("overview")) {
      return "/dashboard";
    }

    if (action.includes("document") || action.includes("signer")) {
      return "/documents";
    }

    return null;
  };

  const handleOpenNotification = async (item) => {
    try {
      if (!item.read) {
        setMarkingReadId(item.id);
        await markNotificationRead(item.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)),
        );
      }

      setShowNotifications(false);

      const targetPath = resolveNotificationTarget(item);
      if (targetPath) {
        navigate(targetPath);
      }
    } catch (e) {
      toast.error("Failed to update notification.");
    } finally {
      setMarkingReadId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setMarkingAllRead(true);
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success("All notifications marked as read.");
    } catch (e) {
      toast.error("Failed to mark all notifications as read.");
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleToggleNotifications = async (e) => {
    e.stopPropagation();
    setShowProfileMenu(false);
    setShowNotifications((prev) => !prev);

    if (!showNotifications) {
      await loadNotifications(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const profileName = session?.user?.name || "Admin User";
  const profileRole = session?.user?.role || "Operations";
  const profileUsername = session?.user?.username || "admin";
  const profileInitial = profileName.charAt(0).toUpperCase() || "A";

  return (
    <div className="admin-layout">
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`admin-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="admin-sidebar-mobile-top">
          <div className="admin-brand">
            <div className="admin-brand-title">WOW-Sign</div>
            <div className="admin-brand-subtitle">Document Signing Management System</div>
          </div>

          <button
            className="sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <Icon icon="akar-icons:cross" width="24" height="24" />
          </button>
        </div>

        <div className="admin-brand admin-brand-desktop">
          <div className="admin-brand-title">WOW-Sign</div>
          <div className="admin-brand-subtitle">Document Signing Management System</div>
        </div>

        <div className="sidebar-section-title">Workspace</div>

        <nav className="sidebar-nav">
          <NavLink
            to="/dashboard"
            className={navClass}
            onClick={handleNavClick}
          >
            <Icon
              icon="material-symbols:dashboard-outline"
              width="24"
              height="24"
            />
            Dashboard
          </NavLink>

          <NavLink
            to="/documents"
            className={navClass}
            onClick={handleNavClick}
          >
            <Icon icon="pajamas:documents" width="24" height="24" /> Documents
          </NavLink>

          <NavLink
            to="/analytics"
            className={navClass}
            onClick={handleNavClick}
          >
            <Icon icon="icon-park-solid:analysis" width="24" height="24" />
            Analytics
          </NavLink>
        </nav>

        <div className="sidebar-guide-block">
          <div className="sidebar-section-title">Guide</div>
          <div className="sidebar-helper">
            Use Dashboard for overview, Documents for operational work, and
            Analytics for signer engagement and SLA insights.
          </div>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-header">
          <div className="admin-header-left-row">
            <button
              className="sidebar-toggle-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <Icon icon="charm:menu-hamburger" width="24" height="24" />
            </button>

            <div className="admin-header-left">
              <h1 className="admin-header-title">{title}</h1>
              <p className="admin-header-subtitle">{subtitle}</p>
            </div>
          </div>

          <div className="admin-header-right">
            <div
              className="header-search-wrap"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="header-search-box">
                <span className="header-search-icon">
                  <Icon
                    icon="material-symbols:search-rounded"
                    width="18"
                    height="18"
                  />
                </span>
                <input
                  className="header-search-input"
                  placeholder="Search documents..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={handleSearchKeyDown}
                />
              </div>

              {searchOpen && search.trim() && (
                <div className="header-search-dropdown">
                  {filteredResults.length > 0 ? (
                    filteredResults.map((doc) => (
                      <button
                        key={doc.id}
                        className="header-search-result"
                        onClick={() => handleOpenResult(doc)}
                      >
                        <div className="header-search-result-title">
                          {doc.name}
                        </div>
                        <div className="header-search-result-meta">
                          <span>{doc.status}</span>
                          <span>Revision {doc.revisionNo || 1}</span>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="header-search-empty">
                      No matching documents found.
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              className="header-icon-btn"
              onClick={() => setShowHelp(true)}
              title="Help"
              aria-label="Help"
            >
              ?
            </button>

            <div
              className="notification-wrap"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="header-icon-btn notification-btn"
                title="Notifications"
                aria-label="Notifications"
                onClick={handleToggleNotifications}
              >
                <Icon icon="mdi:bell-outline" width="24" height="24" />
                {unreadCount > 0 && (
                  <span className="notification-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="notification-dropdown">
                  <div className="notification-dropdown-header">
                    <div>
                      <div className="notification-dropdown-title">
                        Notifications
                      </div>
                      <div className="notification-dropdown-subtitle">
                        Recent workflow updates
                      </div>
                    </div>

                    <div className="notification-header-actions">
                      <span
                        className={`notification-live-dot ${notificationLiveConnected ? "live" : "offline"}`}
                      >
                        {notificationLiveConnected ? "Live" : "Offline"}
                      </span>

                      {notifications.length > 0 && unreadCount > 0 && (
                        <button
                          className="notification-mark-read-btn"
                          onClick={handleMarkAllRead}
                          disabled={markingAllRead}
                        >
                          {markingAllRead ? "..." : "Mark all read"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="notification-list">
                    {notificationsLoading ? (
                      <div className="notification-empty">
                        Loading notifications...
                      </div>
                    ) : notifications.length > 0 ? (
                      notifications.map((item) => (
                        <button
                          key={item.id}
                          className={`notification-item ${
                            item.read ? "read" : "unread"
                          }`}
                          onClick={() => handleOpenNotification(item)}
                          disabled={markingReadId === item.id}
                        >
                          <div className={`notification-dot ${item.type}`} />
                          <div className="notification-content">
                            <div className="notification-title-row">
                              <div className="notification-title">
                                {item.title}
                              </div>
                              {!item.read && (
                                <span className="notification-unread-pill">
                                  New
                                </span>
                              )}
                            </div>
                            <div className="notification-message">
                              {item.message}
                            </div>
                            <div className="notification-time">
                              {formatRelativeDate(item.createdAt)}
                            </div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="notification-empty">
                        No notifications available right now.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              className="profile-chip-wrapper"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="profile-chip"
                onClick={() => {
                  setShowNotifications(false);
                  setShowProfileMenu((prev) => !prev);
                }}
              >
                <span className="profile-avatar">{profileInitial}</span>
                <span className="profile-meta">
                  <span className="profile-name">{profileName}</span>
                  <span className="profile-role">{profileRole}</span>
                </span>
              </button>

              {showProfileMenu && (
                <div className="profile-dropdown">
                  <div className="profile-dropdown-item">
                    Signed in as <strong>{profileUsername}</strong>
                  </div>
                  <div className="profile-dropdown-item muted">
                    Role: {profileRole}
                  </div>
                  <button className="profile-dropdown-action" onClick={handleLogout}>
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="admin-content">{children}</div>
      </main>

      {showHelp && (
        <div className="modal-overlay" onClick={() => setShowHelp(false)}>
          <div
            className="modal-card"
            style={{ maxWidth: 620 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3 className="modal-title">Help & Guidance</h3>
                <p className="modal-subtitle">
                  Quick orientation for using the admin console.
                </p>
              </div>

              <button
                className="modal-close-btn"
                onClick={() => setShowHelp(false)}
                aria-label="Close help"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="activity-feed">
                <div className="activity-feed-item">
                  <div className="activity-dot" />
                  <div>
                    <div className="activity-title">Dashboard</div>
                    <div className="activity-text">
                      Use this for quick actions, recent documents, and
                      operational overview.
                    </div>
                  </div>
                </div>

                <div className="activity-feed-item">
                  <div className="activity-dot" />
                  <div>
                    <div className="activity-title">Documents</div>
                    <div className="activity-text">
                      Upload PDFs, search the document library, and open
                      document workspaces.
                    </div>
                  </div>
                </div>

                <div className="activity-feed-item">
                  <div className="activity-dot" />
                  <div>
                    <div className="activity-title">Analytics</div>
                    <div className="activity-text">
                      Review SLA metrics, drop-off trends, and overall workflow
                      performance.
                    </div>
                  </div>
                </div>

                <div className="activity-feed-item">
                  <div className="activity-dot" />
                  <div>
                    <div className="activity-title">Workspace</div>
                    <div className="activity-text">
                      Inside each document, manage signers, field placement,
                      audit, progress, and revisions.
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowHelp(false)}
                >
                  Got It
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
